import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import dayjs from 'dayjs';
import {
	MonitoringPipelineGoliveItem,
	useMonitoringPipelineGoliveSearch,
} from '@/libs/fleet-api/services/monitoring-pipeline-golive';
import { v2GetUserLists } from '@/libs/fleet-api/services/users';
import { EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Divider, Form, Row, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC, useState } from 'react';
import MonitoringPipelineGoliveDetailModal from './MonitoringPipelineGoliveDetailModal';

const MonitoringPipelineGolivePage: FC = () => {
	const [form] = Form.useForm();
	const [filters, setFilters] = useState<{
		plafondType?: number | null;
		fbdhId?: number | null;
	}>({
		plafondType: null,
		fbdhId: null,
	});

	const [selectedItem, setSelectedItem] =
		useState<MonitoringPipelineGoliveItem | null>(null);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

	const { data, isLoading } = useMonitoringPipelineGoliveSearch(filters);

	const { data: fbdhList } = useQuery({
		queryKey: ['fbdh-list'],
		queryFn: () => v2GetUserLists({ role: 'FBDH' }),
	});

	const fbdhOptions =
		fbdhList?.data?.map((user) => ({
			label: user.fullName || user.userName,
			value: user.userId, // userId is string
		})) ?? [];

	const handleSearch = (values: {
		plafondType?: string | number;
		fbdhId?: string;
	}) => {
		setFilters({
			plafondType:
				values.plafondType === 'all' ? null : (values.plafondType as number),
			fbdhId: values.fbdhId === 'all' ? null : Number(values.fbdhId),
		});
	};

	const formatCurrency = (value: number) => {
		return value?.toLocaleString('id-ID');
	};

	const columns: ColumnsType<MonitoringPipelineGoliveItem> = [
		{
			title: 'No',
			key: 'no',
			render: (_, __, index) => index + 1,
			width: 60,
			align: 'center',
		},
		{
			title: 'View',
			key: 'view',
			width: 60,
			align: 'center',
			render: (_, record) => (
				<Tooltip title="View Details">
					<Button
						type="text"
						icon={<EyeOutlined style={{ color: '#1557BC' }} />}
						onClick={() => {
							setSelectedItem(record);
							setIsDetailModalOpen(true);
						}}
					/>
				</Tooltip>
			),
		},
		{
			title: 'FBDH',
			dataIndex: 'fbdhName',
			key: 'fbdhName',
		},
		{
			title: 'Total Last Month',
			children: [
				{
					title: 'Cetak PO',
					dataIndex: 'totalLastMonthCetakPo',
					key: 'totalLastMonthCetakPo',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
				{
					title: 'Proses Pencairan',
					dataIndex: 'totalLastMonthProsesPencairan',
					key: 'totalLastMonthProsesPencairan',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
			],
		},
		{
			title: 'Total Current MTD',
			children: [
				{
					title: 'Cetak PO',
					dataIndex: 'totalCurrentMtdCetakPo',
					key: 'totalCurrentMtdCetakPo',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
				{
					title: 'Proses Pencairan',
					dataIndex: 'totalCurrentMtdProsesPencairan',
					key: 'totalCurrentMtdProsesPencairan',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
			],
		},
		{
			title: 'Joint Financing',
			children: [
				{
					title: 'Cetak PO',
					dataIndex: 'jfCetakPo',
					key: 'jfCetakPo',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
				{
					title: 'Proses Pencairan',
					dataIndex: 'jfProsesPencairan',
					key: 'jfProsesPencairan',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
			],
		},
		{
			title: 'Non Joint Financing',
			children: [
				{
					title: 'Cetak PO',
					dataIndex: 'njfCetakPo',
					key: 'njfCetakPo',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
				{
					title: 'Proses Pencairan',
					dataIndex: 'njfProsesPencairan',
					key: 'njfProsesPencairan',
					align: 'right',
					render: (value) => formatCurrency(value),
				},
			],
		},
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<FleetTitle>Pipeline Golive</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={handleSearch}
				initialValues={{
					plafondType: 'all',
					fbdhId: 'all',
				}}
			>
				<Row gutter={[40, 20]}>
					<Col span={10}>
						<Form.Item label="Last Update">
							<strong>
								{data?.data?.lastUpdate
									? dayjs(data?.data?.lastUpdate).format('DD/MM/YYYY HH:mm:ss')
									: '-'}
							</strong>
						</Form.Item>
					</Col>
					<Col span={14}></Col>

					<Col span={10}>
						<Form.Item
							label="Jenis Plafon"
							name="plafondType"
						>
							<Select
								options={[
									{ label: 'All', value: 'all' },
									{ label: 'Joint Financing', value: 0 },
									{ label: 'Non Joint Financing', value: 1 },
								]}
							/>
						</Form.Item>
					</Col>
					<Col span={14}></Col>

					<Col span={10}>
						<Form.Item
							label="FBDH"
							name="fbdhId"
						>
							<Select
								showSearch
								optionFilterProp="label"
								options={[{ label: 'All', value: 'all' }, ...fbdhOptions]}
							/>
						</Form.Item>
					</Col>

					<Col span={5}>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								loading={isLoading}
								block
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
					<Col span={9}></Col>
				</Row>
			</Form>
			<Divider />

			<div>
				<div style={{ marginBottom: 16 }}>
					<strong>List Pipeline Golive Debitur</strong>
				</div>
				<Table
					columns={columns}
					dataSource={data?.data?.items ?? []}
					loading={isLoading}
					pagination={false}
					scroll={{ x: 'max-content' }}
					size="small"
					bordered
					summary={() => {
						const grandTotal = data?.data?.grandTotal;
						if (!grandTotal) return null;

						return (
							<Table.Summary.Row
								style={{ fontWeight: 'bold', backgroundColor: '#e6f4ff' }}
							>
								<Table.Summary.Cell
									index={0}
									colSpan={3}
									align="center"
								>
									Grand Total
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={1}
									align="right"
								>
									{formatCurrency(grandTotal.totalLastMonthCetakPo)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={2}
									align="right"
								>
									{formatCurrency(grandTotal.totalLastMonthProsesPencairan)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={3}
									align="right"
								>
									{formatCurrency(grandTotal.totalCurrentMtdCetakPo)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={4}
									align="right"
								>
									{formatCurrency(grandTotal.totalCurrentMtdProsesPencairan)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={5}
									align="right"
								>
									{formatCurrency(grandTotal.jfCetakPo)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={6}
									align="right"
								>
									{formatCurrency(grandTotal.jfProsesPencairan)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={7}
									align="right"
								>
									{formatCurrency(grandTotal.njfCetakPo)}
								</Table.Summary.Cell>
								<Table.Summary.Cell
									index={8}
									align="right"
								>
									{formatCurrency(grandTotal.njfProsesPencairan)}
								</Table.Summary.Cell>
							</Table.Summary.Row>
						);
					}}
				/>
			</div>

			<MonitoringPipelineGoliveDetailModal
				visible={isDetailModalOpen}
				onClose={() => setIsDetailModalOpen(false)}
				data={selectedItem?.details ?? []}
				title={selectedItem?.fbdhName}
			/>
		</div>
	);
};

export default MonitoringPipelineGolivePage;
